import { useQueryClient } from "@tanstack/react-query";
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { flushSync } from "react-dom";

import {
	ButtonStandard,
	FormField,
	FormHandle,
	FormTextEditor,
	InputText,
	PYForm,
	toast,
	usePyForm,
} from "@performyard/py-components";
import { MAX_ROLE_DESCRIPTION_LENGTH } from "@performyard/service-contracts";

import { HasChangesBridge } from "../../../../components/PYFormBridges/HasChangesBridge";
import { setFieldServerError } from "../../../../components/PYFormBridges/setFieldServerError";
import RoleCompetencyList from "./RoleCompetencyList";

import { rolesKeys, useCheckRoleName, useCreateRole, useUpdateRole } from "../../../../api/hooks/defs/roles";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import useNavigate from "../../../../hooks/useNavigate";
import useUnsavedChanges from "../../../../hooks/useUnsavedChanges";

import { checkRoleName } from "../../../../api/services/defs/roles";

import { ROLES_LIST_PATH, type RoleFormValues, roleFormSchema, toRequestPayload } from "./validationSchema";

const NAME_COLLISION_MESSAGE = "Role name already exists";

interface NameErrorBridgeHandle {
	setNameError: (message: string) => void;
}

const NameErrorBridge = forwardRef<NameErrorBridgeHandle>((_, ref) => {
	const pyForm = usePyForm();
	useImperativeHandle(
		ref,
		() => ({
			setNameError: (message: string) => setFieldServerError(pyForm, "name", message),
		}),
		[pyForm]
	);
	return null;
});
NameErrorBridge.displayName = "NameErrorBridge";

export interface RoleFormProps {
	mode: "create" | "edit";
	roleId?: string;
	initialValues: RoleFormValues;
}

const RoleForm: React.FC<RoleFormProps> = ({ mode, roleId, initialValues }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const formRef = useRef<FormHandle<typeof roleFormSchema>>(null);
	const nameErrorRef = useRef<NameErrorBridgeHandle>(null);
	const { setIsUnsafe, setIsSafe } = useUnsavedChanges();

	const [nameDraft, setNameDraft] = useState(initialValues.name);
	const debouncedName = useDebouncedValue(nameDraft.trim(), 500);
	const initialNameTrimmed = initialValues.name.trim();
	const shouldCheckName = debouncedName.length > 0 && debouncedName !== initialNameTrimmed;
	useCheckRoleName(shouldCheckName ? debouncedName : "", roleId);

	const createMutation = useCreateRole();
	const updateMutation = useUpdateRole();
	const isSubmitting = createMutation.isPending || updateMutation.isPending;

	const handleSubmit = async ({ value }: { value: RoleFormValues; resetForm: () => void }) => {
		// Route name-collision errors through the bridge — it sets isTouched alongside serverError; PYForm's submit-handler setError only writes serverError, which FormField hides until isTouched is true.
		const trimmedName = value.name.trim();
		if (trimmedName.length > 0 && trimmedName !== initialNameTrimmed) {
			const result = await queryClient.ensureQueryData({
				queryKey: rolesKeys.checkName(trimmedName, roleId),
				queryFn: () => checkRoleName(trimmedName, roleId),
				staleTime: 60_000,
			});
			if (!result.available) {
				nameErrorRef.current?.setNameError(NAME_COLLISION_MESSAGE);
				return;
			}
		}

		const payload = toRequestPayload(value);
		try {
			if (mode === "edit" && roleId) {
				await updateMutation.mutateAsync({ id: roleId, body: payload });
				toast.success("Changes saved");
			} else {
				await createMutation.mutateAsync(payload);
				toast.success("Role created");
			}
			flushSync(() => setIsSafe());
			navigate(ROLES_LIST_PATH);
		} catch (error) {
			const err = error as { response?: { status?: number }; data?: { message?: string } };
			if (err.response?.status === 409) {
				nameErrorRef.current?.setNameError(NAME_COLLISION_MESSAGE);
			} else {
				toast.error(err.data?.message || "Something went wrong");
			}
		}
	};

	const primaryLabel = mode === "edit" ? "Save Changes" : "Create Role";

	return (
		<PYForm
			ref={formRef}
			id="role-form"
			schema={roleFormSchema}
			defaultValues={initialValues}
			onSubmit={handleSubmit}
			validateOn={["onSubmit"]}
		>
			<NameErrorBridge ref={nameErrorRef} />
			<HasChangesBridge onHasChanges={setIsUnsafe} />
			<div className="flex flex-col gap-4">
				<FormField<string>
					id="name"
					label="Role Name"
					validators={{
						onChange: ({ fieldApi }) => {
							if (fieldApi.state.meta.serverError) {
								fieldApi.state.meta.serverError = undefined;
							}
						},
					}}
					input={(field) => (
						<InputText
							id="name"
							value={field.value}
							onChange={(val) => {
								field.onChange(val);
								setNameDraft(val);
							}}
							onBlur={field.onBlur}
							placeholder="Your text here"
						/>
					)}
				/>

				<FormTextEditor
					id="description"
					label="Description"
					characterLimit={MAX_ROLE_DESCRIPTION_LENGTH}
					placeholder="Description"
					minHeight={60}
					maxHeight={120}
				/>

				<RoleCompetencyList />
			</div>

			{/* Shared grid template with the picker — Cancel sits under the Competency column,
			    Create Role spans the Expected Level + Add button columns. */}
			<div className="mt-6 grid grid-cols-[1fr_192px_76px] gap-3">
				<ButtonStandard
					variant="outline"
					color="neutral"
					size="large"
					onClick={() => navigate(ROLES_LIST_PATH)}
				>
					Cancel
				</ButtonStandard>
				<div className="col-span-2">
					<ButtonStandard
						variant="solid"
						color="primary"
						size="large"
						type="button"
						disabled={isSubmitting}
						onClick={async () => {
							const trimmedName = nameDraft.trim();
							if (trimmedName.length > 0 && trimmedName !== initialNameTrimmed) {
								const cached = queryClient.getQueryData<{ available: boolean }>(
									rolesKeys.checkName(trimmedName, roleId)
								);
								if (cached?.available === false) {
									nameErrorRef.current?.setNameError(NAME_COLLISION_MESSAGE);
									return;
								}
							}

							const isValid = await formRef.current?.validate(true);
							if (isValid) {
								await formRef.current?.submit();
							}
						}}
					>
						{primaryLabel}
					</ButtonStandard>
				</div>
			</div>
		</PYForm>
	);
};

export default RoleForm;
